import { useMemo, useState } from 'react';
import { Button, Checkbox, FormControlLabel, InputAdornment, TextField } from '@mui/material';
import SearchTwoTone from '@mui/icons-material/SearchTwoTone';
import SettingsBackupRestoreTwoTone from '@mui/icons-material/SettingsBackupRestoreTwoTone';
import {
  ColumnsManagementPropsOverrides,
  GridColDefExtended,
  GridColumnsManagementProps,
  GridValidRowModel,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid-pro';
import { getColumnLabel } from './types';

import styles from './ColumnsManagement.module.css';

/**
 * Replaces the grid's built-in Manage Columns panel, which labels every row from headerName — on the Dispatch
 * Board that is the '|||' delimited string ExportButton splits into CSV columns, not a name a dispatcher reads.
 *
 * Listing is driven off hideable, so a column marked `hideable: false` is left out of the panel entirely rather
 * than listed with a disabled toggle the way the built-in panel does it.
 */
export default function ColumnsManagement({ onResetColumnWidths }: GridColumnsManagementProps & ColumnsManagementPropsOverrides) {
  const apiRef = useGridApiContext();
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  /** Widened because a column absent from the model is visible, which the model's Record<string, boolean> does not express */
  const visibilityModel: Record<string, boolean | undefined> = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const [searchText, setSearchText] = useState('');

  const togglableColumns = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return columns.reduce<{ field: string; label: string }[]>((accumulator, column) => {
      const extended: GridColDefExtended<GridValidRowModel> = column;
      // Companion sort columns are an implementation detail of sub-value sorting (see expandSubColumns)
      if (column.hideable === false || extended.subColumnOf) return accumulator;

      const label = getColumnLabel(extended);
      if (label.toLowerCase().includes(search)) {
        accumulator.push({ field: column.field, label });
      }

      return accumulator;
    }, []);
  }, [columns, searchText]);

  return (
    <div className={styles.panel}>
      <TextField
        size='small'
        type='search'
        autoFocus
        placeholder='Find Column'
        value={searchText}
        onChange={(event) => {
          setSearchText(event.target.value);
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <SearchTwoTone fontSize='small' />
              </InputAdornment>
            )
          }
        }}
      />

      <div className={styles.list}>
        {togglableColumns.length === 0 ? (
          <span className={styles.empty}>No Matching Columns</span>
        ) : (
          togglableColumns.map((column) => (
            <FormControlLabel
              key={column.field}
              className={styles.item}
              label={column.label}
              control={
                <Checkbox
                  size='small'
                  checked={visibilityModel[column.field] !== false}
                  onChange={(event) => {
                    apiRef.current.setColumnVisibility(column.field, event.target.checked);
                  }}
                />
              }
            />
          ))
        )}
      </div>

      {onResetColumnWidths && (
        <Button size='small' startIcon={<SettingsBackupRestoreTwoTone fontSize='small' />} onClick={onResetColumnWidths}>
          Reset Column Widths
        </Button>
      )}
    </div>
  );
}
